import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Delete } from '@tunasong/icons'
import type { GearCategory, GearRequirement, MonitorPreference, ProfileGear } from '@tunasong/schemas'
import { gearCategoryValues, monitorPreferenceValues } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import { useMemo, type FC } from 'react'
import { useCallback, useState } from 'react'

const defaultGearRequirement: GearRequirement = {
  category: 'microphone',
  name: '',
  quantity: 1,
  providedByArtist: false,
}

const categoryLabels: Record<GearCategory, string> = {
  microphone: 'Microphone',
  monitor: 'Monitor',
  stand: 'Stand',
  instrument: 'Instrument',
  backline: 'Backline',
  accessory: 'Accessory',
  other: 'Other',
}

const monitorLabels: Record<MonitorPreference, string> = {
  wedge: 'Wedge Monitor',
  iem: 'In-Ear Monitor (IEM)',
  both: 'Both Wedge and IEM',
  none: 'No Monitor',
}

export const GearConfig: FC = () => {
  const [gear, updateGear] = useUserConfig('gear')
  const [newItem, setNewItem] = useState<GearRequirement>(defaultGearRequirement)

  const currentGear: ProfileGear = useMemo(() => gear ?? { requirements: [] }, [gear])

  const handleUpdateField = useCallback(
    <K extends keyof ProfileGear>(field: K, value: ProfileGear[K]) => {
      updateGear({ ...currentGear, [field]: value })
    },
    [currentGear, updateGear]
  )

  const handleAddRequirement = useCallback(() => {
    if (!newItem.name.trim()) {
      return
    }
    const requirements = [...(currentGear.requirements ?? []), newItem]
    updateGear({ ...currentGear, requirements })
    setNewItem(defaultGearRequirement)
  }, [currentGear, newItem, updateGear])

  const handleRemoveRequirement = useCallback(
    (index: number) => {
      const requirements = currentGear.requirements?.filter((_, i) => i !== index) ?? []
      updateGear({ ...currentGear, requirements })
    },
    [currentGear, updateGear]
  )

  return (
    <Box>
      <Typography variant="h6" sx={{ my: 2 }}>
        Gear Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure your default gear requirements for technical riders. These settings will be used when generating
        riders for bandspaces you&apos;re a member of.
      </Typography>

      {/* Role */}
      <TextField
        fullWidth
        label="Role / Position"
        placeholder="e.g., Vocals, Lead Guitar, Drums"
        value={currentGear.role ?? ''}
        onChange={e => handleUpdateField('role', e.target.value || undefined)}
        sx={{ mb: 3 }}
      />

      {/* Monitor Preference */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Monitor Preference</InputLabel>
        <Select
          value={currentGear.monitorPreference ?? ''}
          label="Monitor Preference"
          onChange={e => handleUpdateField('monitorPreference', (e.target.value as MonitorPreference) || undefined)}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {monitorPreferenceValues.map(pref => (
            <MenuItem key={pref} value={pref}>
              {monitorLabels[pref]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Notes */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Notes for Sound Engineers"
        placeholder="Any special requirements or preferences..."
        value={currentGear.notes ?? ''}
        onChange={e => handleUpdateField('notes', e.target.value || undefined)}
        sx={{ mb: 3 }}
      />

      {/* Gear Requirements */}
      <Typography variant="h6" sx={{ my: 2 }}>
        Gear Requirements
      </Typography>

      {currentGear.requirements?.length ? (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Provided</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentGear.requirements.map((req, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip size="small" label={categoryLabels[req.category]} />
                </TableCell>
                <TableCell>
                  {req.name}
                  {req.notes ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {req.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{req.quantity}</TableCell>
                <TableCell>{req.providedByArtist ? 'By artist' : 'By venue'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleRemoveRequirement(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No gear requirements added yet.
        </Typography>
      )}

      {/* Add new requirement */}
      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={newItem.category}
            label="Category"
            onChange={e => setNewItem({ ...newItem, category: e.target.value as GearCategory })}
          >
            {gearCategoryValues.map(cat => (
              <MenuItem key={cat} value={cat}>
                {categoryLabels[cat]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Item name"
          placeholder="e.g., SM58"
          value={newItem.name}
          onChange={e => setNewItem({ ...newItem, name: e.target.value })}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Qty"
          type="number"
          value={newItem.quantity}
          onChange={e => setNewItem({ ...newItem, quantity: parseInt(e.target.value, 10) || 1 })}
          sx={{ width: 70 }}
          slotProps={{ htmlInput: { min: 1 } }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Provided by</InputLabel>
          <Select
            value={newItem.providedByArtist ? 'artist' : 'venue'}
            label="Provided by"
            onChange={e => setNewItem({ ...newItem, providedByArtist: e.target.value === 'artist' })}
          >
            <MenuItem value="venue">Venue</MenuItem>
            <MenuItem value="artist">Artist</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddRequirement} disabled={!newItem.name.trim()}>
          Add
        </Button>
      </Stack>

      <TextField
        size="small"
        fullWidth
        label="Notes for this item"
        placeholder="e.g., boom stand preferred"
        value={newItem.notes ?? ''}
        onChange={e => setNewItem({ ...newItem, notes: e.target.value || undefined })}
        sx={{ mb: 2 }}
      />
    </Box>
  )
}

export default GearConfig
